import React, { useEffect, useMemo, useRef, useState } from "react";
import TeacherAssignForm from "./TeacherAssignForm";
import TeacherSelectForm from "./TeacherSelectForm";
import CommonTable from "../common/commonTable";
import "../../styles/admin/teacher.scss";
import TeacherForm from "./TeacherForm";
import EditTeacherSubjectsModal from "./EditTeacherSubjectsModal";
import EditIcon from "@mui/icons-material/Edit";
import ResetPasswordPopup from "../common/ResetPasswordPopup";
import {
  getAllInstitutionTeacher,
  createInstitutionTeacher,
  updateInstitutionTeacher,
  deleteInstitutionTeacher,
  getAllInstitutionCourse,
  getAllInstitutionCourseBatch,
  updateInstitutionCourseBatch,
  getOneInstitutionBatchTeacher,
  getAllBatchNotAssignTeacher,
  bulkUploadInstitutionTeacher,
  assignTeacherSubjects,
  getOneBatch,
  resetInstitutionTeacherPassword,
} from "../../helper/apicalls/institution";
import { getSubjectsByCourseKey } from "../../helper/apicalls/course";
import Block from "@mui/icons-material/Block";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Cookies from "js-cookie";
import { useSearchParams } from "react-router-dom";
import SuccessPopup from "../common/SuccessPopup";
import GlobalLoader from "../common/GlobalLoader";
import Pagination from "../common/Pagination";

const BatchCell = ({ batch_count, batches, role }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!batch_count || batch_count === 0 || !batches || batches.length === 0) {
    return (
      <span
        className={`assign-status-badge ${
          role === "admin" ? "" : "assign-status-inactive"
        }`}
      >
        {role === "admin" ? "-" : "Not assigned"}
      </span>
    );
  }

  const firstBatch = batches[0];
  const remainingBatches = batches.slice(1);

  return (
    <div className="batch-cell-wrapper" ref={containerRef}>
      <span className="batch-first-name" title={firstBatch.name}>
        {firstBatch.name}
      </span>
      {batch_count > 1 && (
        <span
          className="batch-count-tag"
          onClick={(e) => {
            e.stopPropagation();
            setShowTooltip(!showTooltip);
          }}
        >
          +{batch_count - 1}
        </span>
      )}
      {showTooltip && (
        <div className="batch-floating-tooltip">
          {remainingBatches.map((b, idx) => (
            <div key={b.id || idx} className="batch-tooltip-item">
              {b.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Teacher = ({ batchInfo = false, batchId }) => {
  const [teachers, setTeachers] = useState([]);
  const authToken = Cookies.get("admin_token");
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentTeacher, setCurrentTeacher] = useState(null);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState("Teacher created successfully");
  const [popupType, setPopupType] = useState("success");
  const [loader, setLoader] = useState(false);
  const [institutionCourseData, setInstitutionCourseData] = useState([]);
  const [batchesList, setBatchesList] = useState([]);
  const [subjectsList, setSubjectsList] = useState([]);
  const [isTeacherPickerOpen, setIsTeacherPickerOpen] = useState(false);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ status: "", role: "" });
  const [isEditSubjectsOpen, setIsEditSubjectsOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [resetPopupOpen, setResetPopupOpen] = useState(false);
  const [resetTargetUser, setResetTargetUser] = useState(null);

  const handleResetPasswordClick = (row) => {
    setResetTargetUser(row);
    setResetPopupOpen(true);
  };

  const handleResetPasswordSubmit = async (newPassword) => {
    if (!resetTargetUser) return;
    setLoader(true);
    try {
      const res = await resetInstitutionTeacherPassword(authToken, resetTargetUser.id, {
        password: newPassword,
      });
      setSuccessMsg(res?.message || "Password reset successfully");
      setPopupType("success");
      setSuccessOpen(true);
    } catch (err) {
      console.error("Failed to reset password:", err);
      setSuccessMsg(err?.message || "Failed to reset password");
      setPopupType("error");
      setSuccessOpen(true);
    } finally {
      setLoader(false);
      setResetPopupOpen(false);
      setResetTargetUser(null);
    }
  };

  useEffect(() => {
    if (batchInfo && batchId) {
      getOneBatch(authToken, batchId)
        .then((batchData) => {
          const key = batchData?.course?.key;
          if (key) {
            fetchCourseSubjects(key);
          }
        })
        .catch((err) => console.error("Error loading batch course for subjects:", err));
    }
  }, [batchInfo, batchId]);
  const searchDebounceRef = useRef(null);
  const fileInputRef = React.useRef(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [isUploadResultOpen, setIsUploadResultOpen] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);

  const handleCopyPassword = (pw) => {
    navigator.clipboard.writeText(pw);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };
  const [batchPage, setBatchPage] = useState(1);
  const [batchLimit, setBatchLimit] = useState(10);

  const handleUploadClick = (e) => {
    e.stopPropagation();
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setLoader(true);
    try {
      const res = await bulkUploadInstitutionTeacher(authToken, file, id);
      await fetchInstitutionTeacher();
      setUploadResult(res);
      setIsUploadResultOpen(true);
    } catch (err) {
      console.error("CSV upload failed", err);
      setSuccessMsg(err?.message || "CSV upload failed");
      setPopupType("error");
      setSuccessOpen(true);
    } finally {
      e.target.value = "";
      setLoader(false);
    }
  };

  const handleDownloadSample = () => {
    const csvContent = "name,email,role\nAlice Johnson,alice@example.com,teacher\nBob Brown,bob@example.com,teacher\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "teacher_bulk_upload_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSearchChange = (val) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(val);
    }, 300);
  };

  const columns = useMemo(() => {
    const cols = [
      { field: "name", label: "Name" },
      { field: "email", label: "Email" },
    ];
    if (!batchInfo) {
      cols.push(
        {
          field: "batch_count",
          label: "Batch",
          align: "center",
          render: (batch_count, row) => (
            <span
              className={`assign-status-badge ${
                batch_count > 0
                  ? "assign-status-active"
                  : "assign-status-inactive"
              }`}
            >
              {batch_count > 0
                ? "Assigned"
                : row.role === "admin"
                ? "-"
                : "Not assigned"}
            </span>
          ),
        },
        {
          field: "batch_count",
          label: "Batch Count",
          align: "center",
          render: (batch_count, row) => (
            <span className="batch-count-display">
              {row.role === "admin" && (!batch_count || batch_count === 0)
                ? "-"
                : batch_count || 0}
            </span>
          ),
        },
        {
          field: "batches",
          label: "Batch Names",
          render: (batches, row) => (
            <BatchCell
              batch_count={row.batch_count}
              batches={batches}
              role={row.role}
            />
          ),
        }
      );
    }
    cols.push({
      field: "role",
      label: "Role",
      render: (v) => v?.charAt(0).toUpperCase() + v?.slice(1),
    });
    cols.push({
      field: "disabled",
      label: "Status",
      align: "center",
      render: (isDisabled) => (
        <span
          className={`status-badge ${
            isDisabled ? "status-inactive" : "status-active"
          }`}
        >
          {isDisabled ? "Disabled" : "Active"}
        </span>
      ),
    });
    if (batchInfo) {
      cols.push({
        field: "subjects",
        label: "Subjects",
        render: (subjects) => {
          if (!subjects || subjects.length === 0) return "-";
          return subjects.map((s) => s.name).join(", ");
        },
      });
    }
    cols.push({
      field: "reset_password",
      label: "Reset Password",
      align: "center",
      render: (_, row) => (
        <button
          className="reset-password-btn-table"
          onClick={(e) => {
            e.stopPropagation();
            handleResetPasswordClick(row);
          }}

        >
          Reset
        </button>
      ),
    });
    return cols;
  }, [batchInfo]);

  const handleEdit = (id, row) => {
    setCurrentTeacher(row);
    setIsFormOpen(true);
  };

  const handleDelete = async (id) => {
    setLoader(true);
    try {
      if (!batchInfo) {
        const teacherDelete = await deleteInstitutionTeacher(authToken, id);
        setSuccessMsg(teacherDelete?.message || "Teacher deleted successfully");
        setPopupType("success");
        setSuccessOpen(true);
        await fetchInstitutionTeacher();
      } else {
        console.log("Remove sfhfijbfkjfbshjfbfhb");
        const updataBatch = await updateInstitutionCourseBatch(
          authToken,
          batchId,
          {
            remove_teachers: [id],
          }
        );
        setSuccessMsg(updataBatch?.message || "Teacher remove successfully");
        setPopupType("success");
        setSuccessOpen(true);
        await fetchBatchTeacher();
      }
    } catch (err) {
      console.error("Failed to delete teacher:", err);
      setSuccessMsg(err?.message || "Failed to delete teacher");
      setPopupType("error");
      setSuccessOpen(true);
    } finally {
      setLoader(false);
    }
  };
  const handleEditSubjectsSubmit = async (subject_ids) => {
    if (!editingTeacher || !batchId) return;
    setLoader(true);
    try {
      await assignTeacherSubjects(authToken, {
        batch_id: batchId,
        teacher_id: editingTeacher.id,
        subject_ids,
      });
      setSuccessMsg("Teacher subjects updated successfully");
      setPopupType("success");
      setSuccessOpen(true);
      setIsEditSubjectsOpen(false);
      setEditingTeacher(null);
      await fetchBatchTeacher();
    } catch (err) {
      console.error("Failed to update teacher subjects:", err);
      setSuccessMsg(err?.message || "Failed to update teacher subjects");
      setPopupType("error");
      setSuccessOpen(true);
    } finally {
      setLoader(false);
    }
  };

  const handleCreateClick = () => {
    setCurrentTeacher(null);
    setIsFormOpen(true);
  };

  const handleAddTeacherClick = async () => {
    if (!batchInfo || !batchId) return;
    setIsTeacherPickerOpen(true);
    setLoader(true);
    try {
      // Fetch not-assigned teachers and the batch's course subjects in parallel
      const [teacherRes, batchData] = await Promise.all([
        getAllBatchNotAssignTeacher(authToken, batchId),
        getOneBatch(authToken, batchId),
      ]);
      const list = Array.isArray(teacherRes)
        ? teacherRes
        : Array.isArray(teacherRes?.data)
        ? teacherRes.data
        : [];
      setAvailableTeachers(list);
      // Load subjects for this batch's course
      const courseKey = batchData?.course?.key;
      if (courseKey) {
        fetchCourseSubjects(courseKey);
      } else {
        setSubjectsList([]);
      }
    } catch (err) {
      console.error("Failed to load teachers for selection:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleAddTeacherClose = () => {
    setIsTeacherPickerOpen(false);
    setSubjectsList([]);
  };

  const handleAddTeacherSubmit = async ({ teacherIds, subject_ids }) => {
    if (!Array.isArray(teacherIds) || teacherIds.length === 0) return;
    setLoader(true);
    try {
      const updataBatch = await updateInstitutionCourseBatch(
        authToken,
        batchId,
        { teachers: teacherIds }
      );
      // Assign subjects per teacher if any were selected
      if (subject_ids && subject_ids.length > 0 && batchId) {
        await Promise.all(
          teacherIds.map((teacherId) =>
            assignTeacherSubjects(authToken, {
              batch_id: batchId,
              teacher_id: teacherId,
              subject_ids,
            })
          )
        );
      }
      setSuccessMsg(updataBatch?.message || "Teachers assigned successfully");
      setPopupType("success");
      setSuccessOpen(true);
      setIsTeacherPickerOpen(false);
      await fetchBatchTeacher();
    } catch (err) {
      console.error("Failed to assign teachers:", err);
      setSuccessMsg(err?.message || "Failed to assign teachers");
      setPopupType("error");
      setSuccessOpen(true);
    } finally {
      setLoader(false);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setCurrentTeacher(null);
  };

  const handleFormSubmit = async (data) => {
    setLoader(true);
    if (currentTeacher) {
      try {
        const teacherId = currentTeacher?.id ?? currentTeacher?._id;
        const res = await updateInstitutionTeacher(authToken, teacherId, data);
        setSuccessMsg(res?.message || "Teacher updated successfully");
        setPopupType("success");
        setSuccessOpen(true);
        await fetchInstitutionTeacher();
        handleFormClose();
      } catch (err) {
        console.error("Failed to update teacher:", err);
        setSuccessMsg(err?.message || "Failed to update teacher");
        setPopupType("error");
        setSuccessOpen(true);
      } finally {
        setLoader(false);
      }
    } else {
      try {
        const payload = { ...data, institution: id };
        const res = await createInstitutionTeacher(authToken, payload);
        setSuccessMsg(res?.message || "Teacher created successfully");
        setPopupType("success");
        setSuccessOpen(true);
        await fetchInstitutionTeacher();
        handleFormClose();
      } catch (err) {
        console.error("Failed to create teacher:", err);
        setSuccessMsg(err?.message || "Failed to create teacher");
        setPopupType("error");
        setSuccessOpen(true);
      } finally {
        setLoader(false);
      }
    }
  };

  const handleOpenAssign = () => {
    if (!selectedTeacherIds?.length) return;
    setIsAssignOpen(true);
  };

  const handleAssignClose = () => setIsAssignOpen(false);

  const toggleStatus = async (row) => {
    setLoader(true);
    try {
      const res = await updateInstitutionTeacher(authToken, row?.id, {
        disabled: !row?.disabled,
      });
      setSuccessMsg(res?.message || "Teacher updated successfully");
      setPopupType("success");
      setSuccessOpen(true);
      await fetchInstitutionTeacher();
    } catch (err) {
      console.error("Failed to toggle status:", err);
      setSuccessMsg(err?.message || "Failed to update status");
      setPopupType("error");
      setSuccessOpen(true);
    } finally {
      setLoader(false);
    }
  };

  const handleAssignSubmit = async ({ teacherIds, courseId, batchId, subject_ids }) => {
    try {
      // 1. Assign teachers to batch
      const updataBatch = await updateInstitutionCourseBatch(
        authToken,
        batchId,
        { teachers: teacherIds }
      );

      // 2. For each selected teacher, create/update their subject assignment
      if (subject_ids && subject_ids.length > 0 && batchId) {
        await Promise.all(
          teacherIds.map((teacherId) =>
            assignTeacherSubjects(authToken, {
              batch_id: batchId,
              teacher_id: teacherId,
              subject_ids,
            })
          )
        );
      }

      setSuccessMsg(updataBatch?.message || "Update batch teacher list");
      setPopupType("success");
      setSuccessOpen(true);
      setIsAssignOpen(false);
      setSelectedTeacherIds([]);
    } catch (err) {
      console.error("Failed to update batch teachers:", err);
      setSuccessMsg(err?.message || "Failed to update batch teachers");
      setPopupType("error");
      setSuccessOpen(true);
    }
  };

  const fetchInstitutionTeacher = async () => {
    setLoader(true);
    try {
      const s = (filters?.status || "").trim();
      const params = {
        page,
        limit,
      };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (filters?.role) {
        params.role = filters?.role;
      }

      if (s === "active") params.disabled = false;
      else if (s === "disabled") params.disabled = true;

      const res = await getAllInstitutionTeacher(
        authToken,
        id,
        "teacher",
        params
      );
      setTeachers(Array.isArray(res?.data) ? res.data : []);
      const tp =
        Number(res?.total_pages) ||
        (Number(res?.total) && Math.ceil(Number(res.total) / limit)) ||
        1;
      setTotalPages(tp);
    } catch (err) {
      console.error("Failed to fetch teachers:", err);
    } finally {
      setLoader(false);
    }
  };

  const fetchInstitutionCourse = async () => {
    try {
      const res = await getAllInstitutionCourse(authToken, id);
      setInstitutionCourseData(res[0]?.course || []);
    } catch (err) {
      console.error("Failed to fetch teachers:", err);
    } finally {
    }
  };

  const fetchCourseBatch = async (key) => {
    setLoader(true);
    try {
      const res = await getAllInstitutionCourseBatch(authToken, id, key);
      setBatchesList(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch batches:", err);
    } finally {
      setLoader(false);
    }
  };

  const fetchCourseSubjects = async (courseKey) => {
    try {
      const res = await getSubjectsByCourseKey(courseKey);
      // getSubjectsByCourseKey returns { data: [...] } or an array
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setSubjectsList(list);
    } catch (err) {
      console.error("Failed to fetch subjects:", err);
      setSubjectsList([]);
    }
  };

  const fetchBatchTeacher = async () => {
    setLoader(true);
    try {
      const s = (filters?.status || "").trim();
      const params = { page: batchPage, limit: batchLimit };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (s === "active") params.disabled = false;
      else if (s === "disabled") params.disabled = true;

      const res = await getOneInstitutionBatchTeacher(
        authToken,
        batchId,
        params
      );
      setTeachers(Array.isArray(res?.data) ? res.data : []);
      const tp =
        Number(res?.total_pages) ||
        (Number(res?.total) && Math.ceil(Number(res.total) / limit)) ||
        1;
      setTotalPages(tp);
    } catch (err) {
      console.error("Failed to fetch teachers:", err);
    } finally {
      setLoader(false);
    }
  };

  const goToPage = (p) => {
    const next = Math.max(1, p);
    setLoader(true);
    if (batchInfo) setBatchPage(next);
    else setPage(next);
  };

  const changePerPage = (pp) => {
    setLoader(true);
    if (batchInfo) {
      setBatchLimit(pp);
      setBatchPage(1);
    } else {
      setLimit(pp);
      setPage(1);
    }
  };

  useEffect(() => {
    if (!batchId) fetchInstitutionCourse();
  }, []);

  useEffect(() => {
    if (!id) return;
    fetchInstitutionTeacher();
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [id, page, searchTerm, filters, limit]);

  useEffect(() => {
    if (!batchId) return;
    (async () => {
      await fetchBatchTeacher();
    })();
  }, [batchId, searchTerm, filters, batchPage, batchLimit]);

  return (
    <div className="teacher-tab-container">
      {loader && <GlobalLoader visible={loader} text="Loading teachers..." />}
      <div className="teacher-tab">
        <CommonTable
          data={teachers}
          columns={columns}
          getRowId={(r) => r.id}
          showToolbar
          title={"Admin / Teachers List"}
          onSearchChange={handleSearchChange}
          showFilter={batchInfo ? false : true}
          actionLabelDelete={!batchInfo ? "Delete" : "Remove"}
          filterSections={[
            {
              key: "status",
              title: "Status",
              multiple: false,
              options: [
                { value: "", label: "All" },
                { value: "active", label: "Active" },
                { value: "disabled", label: "Disabled" },
              ],
            },
            {
              key: "role",
              title: "Role",
              multiple: false,
              options: [
                { value: "", label: "All" },
                { value: "admin", label: "Admin" },
                { value: "teacher", label: "Teacher" },
              ],
            },
          ]}
          filterValues={filters}
          onFilterChange={setFilters}
          extraActions={(row, rowKey) => {
            const items = [];
            if (!batchInfo) {
              if (row.disabled) {
                items.push({
                  key: "enable",
                  label: "Enable",
                  icon: (
                    <CheckCircle
                      fontSize="small"
                      sx={{ color: "var(--color-success)" }}
                    />
                  ),
                  onClick: () => toggleStatus(row),
                });
              } else {
                items.push({
                  key: "disable",
                  label: "Disable",
                  icon: <Block fontSize="small" />,
                  onClick: () => toggleStatus(row),
                  danger: true,
                });
              }
            } else {
              items.push({
                key: "edit-subjects",
                label: "Edit Subjects",
                icon: <EditIcon fontSize="small" />,
                onClick: () => {
                  setEditingTeacher(row);
                  setIsEditSubjectsOpen(true);
                },
              });
            }

            return items;
          }}
          toolbarRight={
            <>
              {!batchInfo && (
                <>
                  <button
                    className="teacher-tab-header-btn btn-interactive"
                    onClick={handleCreateClick}
                  >
                    Create
                  </button>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <button
                      className="teacher-bulk-upload-btn"
                      onClick={handleUploadClick}
                      type="button"
                      title="Upload CSV. Expected columns: name, email, role"
                    >
                      Upload CSV
                    </button>
                    <button
                      onClick={handleDownloadSample}
                      type="button"
                      style={{
                        fontSize: "var(--fs-sm)",
                        padding: "10px 16px",
                        borderRadius: "6px",
                        fontWeight: "500",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        backgroundColor: "var(--bg, #ffffff)",
                        color: "var(--bulk-upload-bg, #4f46e5)",
                        border: "1px solid var(--bulk-upload-bg, #4f46e5)",
                      }}
                      title="Download sample template CSV"
                    >
                      Download Sample CSV
                    </button>
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                  </div>
                  <button
                    className={`teacher-tab-header-btn teacher-btn-interactive btn-interactive`}
                    onClick={handleOpenAssign}
                    disabled={
                      !(selectedTeacherIds && selectedTeacherIds.length > 0)
                    }
                  >
                    Assign
                  </button>
                </>
              )}
              {batchInfo && (
                <>
                  <button
                    className="teacher-tab-header-btn btn-interactive"
                    onClick={handleAddTeacherClick}
                  >
                    Add Teachers
                  </button>
                </>
              )}
            </>
          }
          multiSelect={!batchInfo}
          selectedRowIds={selectedTeacherIds}
          onSelectionChange={setSelectedTeacherIds}
          isRowSelectable={(row) => row.role !== "admin"}
          showActions
          onEdit={handleEdit}
          onDelete={handleDelete}
          maxBodyHeight={`calc(-300px + 100vh)`}
          fixedBodyRows={8}
          isConfirmDeleteOpen={isConfirmDeleteOpen}
          setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
          deleteMessage={
            batchInfo
              ? "Are you sure you want to remove from this batch? This action cannot be undone."
              : "Are you sure you want to delete this item? This action cannot be undone."
          }
          removeActionLabel={batchInfo ? "Remove from Batch" : "Delete"}
          showEditAction={batchInfo ? false : true}
        />
      </div>
      <div className="teacher-pagination">
        <Pagination
          page={batchInfo ? batchPage : page}
          pageCount={totalPages}
          onChange={goToPage}
          perPage={batchInfo ? batchLimit : limit}
          perPageOptions={[5, 10, 25, 100]}
          onPerPageChange={changePerPage}
        />
      </div>
      {successOpen && (
        <SuccessPopup
          open={successOpen}
          onClose={() => setSuccessOpen(false)}
          message={successMsg}
          type={popupType}
        />
      )}
      {isUploadResultOpen && uploadResult && (
        <div className="common-popup-overlay">
          <div className="common-popup-container" style={{ maxWidth: "600px" }}>
            <div className="popup-header with-close">
              <h2>Bulk Upload Report</h2>
              <button className="popup-close-button" onClick={() => setIsUploadResultOpen(false)}>&times;</button>
            </div>
            <div className="popup-content bulk-upload-report-content">
              <div className="summary-cards-container">
                <div className="summary-card success">
                  <div className="count">
                    {uploadResult.created}
                  </div>
                  <div className="label">
                    Created Successfully
                  </div>
                </div>
                <div className={`summary-card ${uploadResult.skipped?.length > 0 ? "skipped" : "no-skipped"}`}>
                  <div className="count">
                    {uploadResult.skipped?.length || 0}
                  </div>
                  <div className="label">
                    Rows Skipped
                  </div>
                </div>
              </div>

              <div className="password-notice-banner">
                <div className="banner-left">
                  <span className="banner-text">
                    Default password for newly created teachers: <strong>teacher123</strong>
                  </span>
                </div>
                <button className="copy-btn" onClick={() => handleCopyPassword("teacher123")}>
                  {passwordCopied ? "Copied!" : "Copy"}
                </button>
              </div>
              
              {uploadResult.skipped?.length > 0 && (
                <div className="skipped-details-section">
                  <h4>Skipped Rows Details</h4>
                  <div className="skipped-table-wrapper">
                    <table className="skipped-table">
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Identifier</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadResult.skipped.map((item, idx) => (
                          <tr key={idx}>
                            <td className="row-col">Row {item.row}</td>
                            <td className="identifier-col">
                              {item.email || item.register_no || "-"}
                            </td>
                            <td className="reason-col">{item.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="popup-actions">
              <button className="popup-submit-button" onClick={() => setIsUploadResultOpen(false)}>Done</button>
            </div>
          </div>
        </div>
      )}
      {isAssignOpen && (
        <TeacherAssignForm
          isOpen={isAssignOpen}
          onClose={handleAssignClose}
          onSubmit={handleAssignSubmit}
          selectedTeacherIds={selectedTeacherIds}
          courses={institutionCourseData}
          batches={batchesList}
          subjects={subjectsList}
          getBatched={fetchCourseBatch}
          getSubjects={fetchCourseSubjects}
        />
      )}
      {isTeacherPickerOpen && (
        <TeacherSelectForm
          isOpen={isTeacherPickerOpen}
          onClose={handleAddTeacherClose}
          onSubmit={handleAddTeacherSubmit}
          teachers={availableTeachers}
          subjects={subjectsList}
          title="Add Teachers"
        />
      )}
      {isFormOpen && (
        <TeacherForm
          isOpen={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          initialData={currentTeacher}
        />
      )}
      {isEditSubjectsOpen && editingTeacher && (
        <EditTeacherSubjectsModal
          isOpen={isEditSubjectsOpen}
          onClose={() => {
            setIsEditSubjectsOpen(false);
            setEditingTeacher(null);
          }}
          onSubmit={handleEditSubjectsSubmit}
          teacherName={editingTeacher.name}
          subjects={subjectsList}
          initialSelectedSubjects={editingTeacher.subjects || []}
        />
      )}
      {resetPopupOpen && resetTargetUser && (
        <ResetPasswordPopup
          isOpen={resetPopupOpen}
          onClose={() => {
            setResetPopupOpen(false);
            setResetTargetUser(null);
          }}
          userName={resetTargetUser.name}
          onResetPassword={handleResetPasswordSubmit}
        />
      )}
    </div>
  );
};

export default Teacher;
